import { SignalsObjectType } from "../../SignalsModels";
import {
    CARD_SIZE_SPECS,
    CHART_TYPE_SIZE_OVERRIDES,
    ENDPOINT_SIZE_OVERRIDES,
} from "./layoutConfig";
import type { CardSizeSpec } from "./layoutConfig";
import type { LayoutObject } from "./types";

/** What decides a card's sizes. The values live in `cardSizes.json`. */
export type CardSizeKey = Pick<
    LayoutObject,
    "objectType" | "chartType" | "dataEndpoint" | "placeholderSize"
>;

/**
 * Card types whose height follows their measured content. Charts are absent
 * because a chart fills whatever box it is given: measuring one returns the
 * height it already has, and a floor taken from that would only ratchet up.
 */
const CONTENT_DRIVEN_OBJECT_TYPES: ReadonlySet<SignalsObjectType> = new Set([
    SignalsObjectType.TextCard,
    SignalsObjectType.Table,
    SignalsObjectType.MontageCard,
    SignalsObjectType.Unique,
]);

export function isContentDrivenCard(objectType: SignalsObjectType): boolean {
    return CONTENT_DRIVEN_OBJECT_TYPES.has(objectType);
}

/**
 * The type's sizes, then the chart type's overrides, then the endpoint's. A
 * placeholder for a hidden card is fixed at the size it holds.
 */
export function getCardSizeSpec({
    objectType,
    chartType,
    dataEndpoint,
    placeholderSize,
}: CardSizeKey): Readonly<CardSizeSpec> {
    if (placeholderSize) {
        const { width, height } = placeholderSize;
        return {
            defaultWidth: width,
            defaultHeight: height,
            minimumWidth: width,
            minimumHeight: height,
            maximumWidth: width,
            maximumHeight: height,
        };
    }

    const spec = CARD_SIZE_SPECS[objectType];
    const chartTypeOverride =
        objectType === SignalsObjectType.Chart && chartType
            ? CHART_TYPE_SIZE_OVERRIDES[chartType]
            : undefined;
    const endpointOverride = dataEndpoint
        ? ENDPOINT_SIZE_OVERRIDES[dataEndpoint]
        : undefined;

    if (!chartTypeOverride && !endpointOverride) return spec;
    return { ...spec, ...chartTypeOverride, ...endpointOverride };
}
